import { useMemo } from 'react';
import { cn, PaletteUtils } from '@shared/utils';
import { type AccountResponse, AccountType } from '@shared/api';
import { AccountTileSmall } from '@widgets';
import { Carousel, NoData } from '@shared/widgets';

interface AccountsGridProps {
  accountType: AccountType;
  accounts: AccountResponse[];
}

export const AccountsGrid = (props: AccountsGridProps) => {
  const pagesWithData = useMemo(() => {
    const filteredAccounts = props.accounts.filter((x) => x.type === props.accountType);

    const mainAccounts = filteredAccounts.filter((account) => account.mainAccId === null);
    const subAccounts = filteredAccounts.filter((account) => account.mainAccId !== null);

    const grouped = mainAccounts.map((mainAccount) => {
      const relatedSubAccounts = subAccounts.filter((account) => account.mainAccId === mainAccount.id);
      return { mainAccount, subAccounts: relatedSubAccounts };
    });

    const orphanSubAccounts = subAccounts.filter((subAccount) => !mainAccounts.some((main) => main.id === subAccount.mainAccId));

    const allGroups = [...grouped, ...orphanSubAccounts.map((subAccount) => ({ mainAccount: subAccount, subAccounts: [] }))];

    const countItemsPerPage = (document.getElementById('root')?.scrollWidth ?? 0 > 1280) ? 6 : 4;
    const countOfSlide = Math.ceil(allGroups.length / countItemsPerPage);

    const result: (typeof allGroups)[] = [];
    for (let i = 0; i < countOfSlide; i++) {
      const startIndex = i * countItemsPerPage;
      const endIndex = startIndex + countItemsPerPage;
      const page = allGroups.slice(startIndex, endIndex);
      result.push(page);
    }

    return result;
  }, [props.accounts, props.accountType]);

  if (pagesWithData.length === 0) return <NoData />;
  return (
    <Carousel>
      {pagesWithData.map((itemsForPage, idx) => (
        <div
          key={idx}
          className={cn(
            '!grid grid-cols-2 items-stretch gap-4 py-1 pr-4 xl:grid-cols-3',
            PaletteUtils.isVcfcPalette && 'max-md:grid-cols-1 max-md:pr-0',
          )}
        >
          {itemsForPage.map(({ mainAccount, subAccounts }) => (
            <AccountTileSmall key={mainAccount.id} account={mainAccount} subAccounts={subAccounts} />
          ))}
        </div>
      ))}
    </Carousel>
  );
};
